/** Upper bound on QIT IDs parsed from query params to prevent
 *  denial-of-service via URL manipulation. */
export const MAX_QIT_IDS_FROM_PARAMS = 100;

export type NavKind = 'question' | 'user_profile_field';

export interface HistoryStep {
  kind: NavKind;
  groupId: number | null;
  answerGroupId: number | null;
  instanceSeq: number;
  qitId: number | null;
  fieldName: string | null;
  status: 'answered' | 'skipped';
}

export type WorkItemType =
  | 'loading'
  | 'question'
  | 'signature'
  | 'stale_document'
  | 'file_upload'
  | 'user_profile_field'
  | 'skip_gate'
  | 'complete'
  | 'nothing_to_do'
  | 'add_another'
  | 'error';

export interface DeferredItems {
  qitIds: Record<string, number[]>; // instanceKey → qitIds deferred in that instance
  signatureIds: number[];
  staleDocIds: number[];
  profileFieldNames: string[];
  fileUploadDismissed: boolean;
}

export interface PendingFileQit {
  qit_id: number;
  question_name: string;
}

export interface QuestionItem {
  qit_id: number;
  order: number;
  question_text: string;
  help_text: string | null;
  question_type: string;
  necessity: number;
  options: Array<{
    id: number;
    label: string;
    requires_file_upload?: boolean;
  }>;
  visible: boolean;
  always_ask: boolean;
  ai_extracted: boolean;
  existing_answer: any;
  answer_group_id: number | null;
  parent_qit_id: number | null;
  parent_choice_id: number | null;
  number_of_files_required_per_answer?: number;
}

export interface ExistingSurveyFile {
  file_id: number;
  file_name: string;
  pdf_file_url: string;
  survey_answer_id: number;
  status: number;
  is_rejected?: boolean;
  created_at?: string | null;
}

export interface NextGroup {
  group_id: number;
  group_name: string;
  section_name: string;
  group_type: 'single' | 'multi';
  effective_weight: number;
  instance_label?: string | null;
  answer_group_ids?: number[];
  questions: QuestionItem[];
}

export interface PendingSignature {
  signature_request_id: number;
  form_name: string;
  assigned_to_user_id: number;
  status: string;
  cert_type_label?: string;
}

export interface StaleDocument {
  survey_file_id: number;
  file_name: string;
  question_label: string;
  stale_reason: string;
  question_text?: string;
  instance_label?: string;
  uploaded_date?: string;
}

export interface UserProfileField {
  field_name: string;
  question_type: string;
  question_text: string;
  help_text: string | null;
  options: Array<{ id: string; label: string }> | null;
  existing_value: any;
  subfields: string[] | null;
}

export interface InterviewState {
  // Identity
  surveyId: number | null;
  onBehalfOf: number | null;
  onlyQitIds: number[] | null;
  answerGroupQits: Record<number, number[]> | null;
  activeAgTarget: number | null;
  certId: number | null;
  certName: string | null;
  propertyName: string | null;
  certCategory: string | null;
  effectiveDate: string | null;

  // Permissions
  canAnswer: boolean;
  canSign: boolean;

  // Routing
  workItemType: WorkItemType;
  errorMessage: string | null;
  backPath: string | null;
  /** Entry intent (ENG-4049). Set only when the renter entered from a
   *  cert-status "questions" action, which asks for the question group even
   *  when pending signatures would otherwise win the work-item priority. */
  requestedWorkItem: 'questions' | null;
  hasStartedInterview: boolean;

  // Group state
  currentGroup: NextGroup | null;
  visibleQuestionQueue: number[]; // QIT IDs
  currentQuestionIndex: number;
  localAnswers: Record<number, any>; // qit_id → answer value
  answersByKey: Record<string, unknown>; // makeStepKey(HistoryStep) → value (durable across groups)
  currentAnswerGroupId: number | null;

  // Deferred
  deferred: DeferredItems;

  // IPE data
  pendingSignatures: PendingSignature[];
  staleDocuments: StaleDocument[];
  userProfileFields: UserProfileField[];
  pendingFileQits: PendingFileQit[];
  interviewComplete: boolean;

  // Group snapshots (retained for back-navigation re-render)
  /** Per-instance group snapshots, keyed by makeGroupInstanceKey (groupId + instance identity). */
  groupsById: Record<string, NextGroup>;
  instanceSeqByGroup: Record<number, number>;
  profileFieldsByName: Record<string, UserProfileField>;

  // Navigation history
  history: HistoryStep[];
  cursor: number;
  verifiedIndex: number;

  // Frontier resync
  frontierResyncNeeded: boolean;

  // Loading
  loading: boolean;
  loadingProgress: number;
  answerInFlight: boolean;
  answerError: string | null;
}

// IPE API response shape
export interface IPEResponse {
  can_answer?: boolean;
  can_sign?: boolean;
  pending_signatures?: PendingSignature[];
  stale_documents?: StaleDocument[];
  user_profile_fields?: UserProfileField[];
  pending_file_qits?: PendingFileQit[];
  interview_complete?: boolean;
  next_group?: NextGroup | null;
  cert_id?: number | null;
  cert_name?: string | null;
  property_name?: string | null;
  cert_category?: string | null;
  effective_date?: string | null;
}
